import { ArrowUpDown, ChevronUp, ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"
import { TableHead } from "@/components/ui/table"

/**
 * Click-to-sort table header. Pure presentational — sort state and the actual
 * comparator live in the consuming page. The component reports the column
 * the user clicked via `onSort(column)`; the parent decides what to do (cycle
 * direction, swap key, clear, etc.).
 *
 * Usage:
 *   <SortableHeader
 *     column="goal"
 *     sortKey={sortKey}
 *     direction={direction}
 *     onSort={(col) => setSort(col)}
 *   >
 *     Goal
 *   </SortableHeader>
 *
 * Three icon states:
 *   - inactive (this header is not the current sort key): muted ArrowUpDown
 *   - active asc: ChevronUp
 *   - active desc: ChevronDown
 */
export function SortableHeader({
  column,
  sortKey,
  direction,
  onSort,
  className,
  children,
  ...props
}) {
  const isActive = sortKey === column
  const Icon = !isActive
    ? ArrowUpDown
    : direction === "desc"
      ? ChevronDown
      : ChevronUp

  return (
    <TableHead
      className={cn("cursor-pointer select-none", className)}
      onClick={() => onSort?.(column)}
      {...props}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <Icon
          className={cn(
            "h-3.5 w-3.5",
            isActive ? "text-zinc-700" : "text-zinc-400"
          )}
          aria-hidden="true"
        />
      </span>
    </TableHead>
  )
}
